// What the conjoint blocks derive from the answer before anything is drawn, ported from the coffee
// block (calcRanks / calcPersonaRanks / calcPackages / the market-simulator seeding / the view
// predicates) and from Conjoint.Lib's design-size rules.

import {
  calcBestPackage,
  calcImportance,
  calcMedianPackage,
  calcPackage,
  calcPositionSum,
  calcWorstPackage,
  indexToPackage
} from '@aytm/app/stats/page/question-views/cjc/lib/calc'

// Below this design size the block draws the rich chart on the full design matrix. Above it the
// default flips to the importance summary, because full rendering becomes prohibitively
// expensive. It is a default only - a pinned cjv value always wins.
const DESIGN_SIZE_THRESHOLD = 100000

export const calcDesignSize = (batches = []) =>
  (batches || [])
    .filter(batch => batch && !batch.na)
    .reduce((acc, batch) => acc * Math.max(1, (batch.answerVariants || []).length), 1)

// Fail safe when the batches are not populated yet (virtual / embedded items, pre-schema race):
// pick the cheaper slice, not the expensive one.
export const defaultCjView = (batches = []) =>
  batches?.length ? (calcDesignSize(batches) < DESIGN_SIZE_THRESHOLD ? 'full' : 'lite') : 'lite'

export const resolveCjView = (batches, pinned) =>
  'lite' === pinned || 'full' === pinned ? pinned : defaultCjView(batches)

export const isKnownPackage = (answer, currentPackage) => {
  const positions = currentPackage?.length ? currentPackage.split('.').map(value => parseInt(value, 10)) : []
  const featureLengths = answer?.featureLengths || []

  return positions.every((position, i) => 0 < position && position <= featureLengths[i])
}

export const isKnownPersona = (answer, persona) =>
  !persona || 'loading' === answer || 0 < (answer?.clusters?.[persona - 1]?.amount || 0)

// The rank order of every package in the design, for one persona. The server sends it when it can;
// this is the client-side fallback the sidebar arrows step through.
const calcPersonaRanks = ({ answer, mode, persona, numberOfCombinations }) => {
  const shares = Array.from({ length: numberOfCombinations })
  const scores = Array.from({ length: numberOfCombinations })

  for (let i = 0; i < numberOfCombinations; i++) {
    const sum = calcPositionSum(mode, answer, persona, indexToPackage(answer, i))
    const exp = Math.exp(sum)

    shares[i] = exp / (1 + exp)
    scores[i] = sum
  }

  const rank = values =>
    values
      .map((value, i) => [value, i])
      .sort((a, b) => b[0] - a[0])
      .map(entry => entry[1])

  return { shares: rank(shares), scores: rank(scores) }
}

// The summarised slice drops the arrays the packages are read from, and it can land a render before the
// verdict that names it -- so the payload itself is checked. A persona reads its own cluster, which the
// same reduction strips on the same pass: a cluster falls to four keys while the cluster COUNT stays put,
// so the count is not a usable test for whether the slice was reduced and the arrays are.
export const isSummarisedPayload = (answer, persona) => {
  const series = persona ? answer?.clusters?.[persona - 1] : answer
  return 1 == (answer?.config?.v ?? 1)
    ? !Array.isArray(series?.adjusted_coefficients)
    : !Array.isArray(series?.tickMarksShares)
}

export const calcRanks = ({ answer, mode, badData, isLiteView } = {}) => {
  if (badData || 'loading' === answer || isLiteView || !answer || isSummarisedPayload(answer)) {
    return { packageRankSharesArr: [], packageRankScoresArr: [] }
  }

  const personaCount = (answer.clusterN?.[0] || 0) + 1
  const numberOfCombinations = (answer.featureLengths || []).reduce((acc, cur) => acc * cur, 1)

  const packageRankSharesArr = Array.from({ length: personaCount }, () => [])
  const packageRankScoresArr = Array.from({ length: personaCount }, () => [])

  for (let i = 0; i < personaCount; i++) {
    const source = 0 === i ? answer : answer.clusters?.[i - 1]
    if (0 !== i && !(0 < (source?.amount || 0))) continue

    if (Array.isArray(source?.packageRankShares) && Array.isArray(source?.packageRankScores)) {
      packageRankSharesArr[i] = source.packageRankShares
      packageRankScoresArr[i] = source.packageRankScores
      continue
    }

    const { shares, scores } = calcPersonaRanks({ answer, mode, persona: i, numberOfCombinations })
    packageRankSharesArr[i] = shares
    packageRankScoresArr[i] = scores
  }

  return { packageRankSharesArr, packageRankScoresArr }
}

// The five packages the sidebar offers (best / upper quartile / median / lower quartile / worst),
// and the package the chart opens on when nothing is stored.
export const calcPackages = ({
  answer,
  mode,
  persona,
  currentPackage,
  badData,
  isLiteView,
  packageRankSharesArr,
  packageRankScoresArr
} = {}) => {
  if (badData || 'loading' === answer || isLiteView || !answer || isSummarisedPayload(answer)) {
    return { currentPackage: '', mainPackages: [] }
  }

  const stored = currentPackage?.length ? currentPackage.split('.').map(value => parseInt(value, 10)) : []

  const medianPackage = calcMedianPackage(mode, answer, persona)
  const bestPackage = calcBestPackage(mode, answer, persona, packageRankSharesArr, packageRankScoresArr)
  const worstPackage = calcWorstPackage(mode, answer, persona, packageRankSharesArr, packageRankScoresArr)

  return {
    currentPackage: (stored.length ? stored : medianPackage).join('.'),
    mainPackages: [
      Array.from(bestPackage),
      calcPackage(mode, answer, 0.75, persona),
      Array.from(medianPackage),
      calcPackage(mode, answer, 0.25, persona),
      Array.from(worstPackage)
    ]
  }
}

// Until the researcher edits the list, the simulator shows the best package.
export const calcMarketSimulatorPackages = ({ packages = [], userChanged, mainPackages = [] } = {}) => {
  if (userChanged || packages.length || !mainPackages.length) return packages

  return [Array.from(mainPackages[0])]
}

// Which batches are expanded, as the sorted position list the cb param carries.
export const calcUncollapsed = (uncollapsed, { position, collapsed } = {}) => {
  const stored =
    'string' === typeof uncollapsed
      ? uncollapsed.split('.').filter(value => 0 < value.length)
      : Array.isArray(uncollapsed)
        ? uncollapsed.map(String)
        : []

  const value = String(position)
  const positions = collapsed ? stored.filter(entry => entry !== value) : [...new Set([...stored, value])]

  return positions.sort().join('.')
}

export const calcAllUncollapsed = (batches = [], collapse) =>
  collapse
    ? ''
    : (batches || [])
        .filter(batch => !batch.na)
        .map(batch => String(batch.position))
        .sort()
        .join('.')

// Lite view renders importance-% only, so the chart-mode space is constrained to the two
// percent-bar orientations; page-level defaults like 'pie' coerce to percents.
export const LITE_CHART_MODES = ['percents', 'bpercents']

export const getLiteChartMode = ({ chartMode, questionChartMode, globalChartMode } = {}) => {
  const mode = ('off' === chartMode ? globalChartMode : chartMode) || questionChartMode || globalChartMode

  return LITE_CHART_MODES.includes(mode) ? mode : 'percents'
}

// The importance-% series the lite chart plots, shaped like an answer-variants answer so the
// shared chart and the exporters' display-range maths can read it.
export const calcLiteChartData = ({ answer, batches, persona } = {}) => {
  if (!answer || 'object' !== typeof answer || !batches?.length) return null

  try {
    const hasCluster = !persona || null != answer.clusters?.[persona - 1]

    const attrs = (batches || [])
      .filter(batch => batch && !batch.na)
      .map(batch => ({
        position: batch.position,
        text: batch.text,
        percents: Math.max(
          0,
          Math.min(100, 100 * ((hasCluster ? calcImportance(answer, batch.position, persona) : 0) || 0))
        )
      }))
      .filter(attr => !isNaN(attr.percents))

    return {
      answerVariants: attrs.map(attr => ({ position: attr.position, text: attr.text })),
      synthesizedAnswer: attrs.reduce(
        (acc, attr) => {
          acc[`a${attr.position}`] = { percents: attr.percents, amount: attr.percents }
          return acc
        },
        { total: hasCluster && persona ? answer.clusters?.[persona - 1]?.amount : answer.total }
      )
    }
  } catch {
    return null
  }
}
